import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  CubeIcon, 
  MapIcon, 
  ViewColumnsIcon
} from '@heroicons/react/24/outline';

const FloorPlanEditor = ({ onPlanChange, initialPlan = null, viewMode, setViewMode }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('wall'); // 'wall', 'door', 'window', 'erase'
  const [walls, setWalls] = useState([]);
  const [doors, setDoors] = useState([]);
  const [windows, setWindows] = useState([]);
  const [cameraPosition, setCameraPosition] = useState({ x: 100, y: 100 }); // For mini map
  const [cameraAngle, setCameraAngle] = useState(0); // Camera direction angle
  const [cameraVerticalAngle, setCameraVerticalAngle] = useState(0); // Vertical angle for up/down
  const [currentLine, setCurrentLine] = useState(null);
  const [scale, setScale] = useState(40); // pixels per meter
  
  // Prevent wheel scroll on canvas and main container
  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const [gridSize, setGridSize] = useState(20);
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [roomDimensions, setRoomDimensions] = useState({ width: 0, height: 0, area: 0 });
  const [currentWallLength, setCurrentWallLength] = useState(0);
  const [floorTexture, setFloorTexture] = useState(null);

  // Load floor texture
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setFloorTexture(img);
    };
    img.src = '/images/WoodFloor01.jpg';
  }, []);

  // Draw floor texture only inside the room area
  const drawFloorTexture = useCallback((ctx, canvas) => {
    // Start with clean background
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    if (!floorTexture || walls.length < 3) return;
    
    ctx.save();
    
    try {
      // Calculate room bounding box from walls
      let minX = Infinity, maxX = -Infinity;
      let minY = Infinity, maxY = -Infinity;
      
      walls.forEach(wall => {
        minX = Math.min(minX, wall.start.x, wall.end.x);
        maxX = Math.max(maxX, wall.start.x, wall.end.x);
        minY = Math.min(minY, wall.start.y, wall.end.y);
        maxY = Math.max(maxY, wall.start.y, wall.end.y);
      });
      
      // Minimal padding to just avoid overlap with wall borders
      const padding = 2; // Very minimal padding
      minX += padding;
      maxX -= padding;
      minY += padding;
      maxY -= padding;
      
      if (maxX > minX && maxY > minY) {
        // Create rectangular room area
        const roomWidth = maxX - minX;
        const roomHeight = maxY - minY;
        
        // Create pattern and fill room area
        const pattern = ctx.createPattern(floorTexture, 'repeat');
        if (pattern) {
          ctx.save();
          
          // Translate to room area and scale pattern
          ctx.translate(minX, minY);
          ctx.scale(0.3, 0.3);
          
          ctx.fillStyle = pattern;
          ctx.fillRect(0, 0, roomWidth / 0.3, roomHeight / 0.3);
          
          ctx.restore();
          
          // No overlay - keep texture fully visible
        }
      }
    } catch (error) {
      console.log('Floor texture rendering error:', error);
    }
    
    ctx.restore();
  }, [floorTexture, walls]);

  // Canvas drawing functions
  const drawGrid = useCallback((ctx, canvas) => {
    if (!showGrid) return;
    
    // Calculate room area to avoid drawing grid inside
    let roomMinX = Infinity, roomMaxX = -Infinity;
    let roomMinY = Infinity, roomMaxY = -Infinity;
    let hasRoom = false;
    
    if (walls.length >= 3) {
      walls.forEach(wall => {
        roomMinX = Math.min(roomMinX, wall.start.x, wall.end.x);
        roomMaxX = Math.max(roomMaxX, wall.start.x, wall.end.x);
        roomMinY = Math.min(roomMinY, wall.start.y, wall.end.y);
        roomMaxY = Math.max(roomMaxY, wall.start.y, wall.end.y);
      });
      
      const padding = 2;
      roomMinX += padding;
      roomMaxX -= padding;
      roomMinY += padding;
      roomMaxY -= padding;
      hasRoom = roomMaxX > roomMinX && roomMaxY > roomMinY;
    }
    
    // Main grid lines
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 0.5;
    
    // Major grid lines every 5 units (meters)
    const majorStep = gridSize * 5;
    
    // Draw minor grid - skip room area
    for (let x = 0; x <= canvas.width; x += gridSize) {
      ctx.beginPath();
      
      if (hasRoom && x >= roomMinX && x <= roomMaxX) {
        // Draw line segments: top part and bottom part, skipping room area
        if (roomMinY > 0) {
          ctx.moveTo(x, 0);
          ctx.lineTo(x, roomMinY);
        }
        if (roomMaxY < canvas.height) {
          ctx.moveTo(x, roomMaxY);
          ctx.lineTo(x, canvas.height);
        }
      } else {
        // Draw full line
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
      }
      ctx.stroke();
    }
    
    for (let y = 0; y <= canvas.height; y += gridSize) {
      ctx.beginPath();
      
      if (hasRoom && y >= roomMinY && y <= roomMaxY) {
        // Draw line segments: left part and right part, skipping room area
        if (roomMinX > 0) {
          ctx.moveTo(0, y);
          ctx.lineTo(roomMinX, y);
        }
        if (roomMaxX < canvas.width) {
          ctx.moveTo(roomMaxX, y);
          ctx.lineTo(canvas.width, y);
        }
      } else {
        // Draw full line
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
      }
      ctx.stroke();
    }
    
    // Draw major grid lines - also skip room area
    ctx.strokeStyle = '#9ca3af';
    ctx.lineWidth = 1;
    
    for (let x = 0; x <= canvas.width; x += majorStep) {
      ctx.beginPath();
      
      if (hasRoom && x >= roomMinX && x <= roomMaxX) {
        if (roomMinY > 0) {
          ctx.moveTo(x, 0);
          ctx.lineTo(x, roomMinY);
        }
        if (roomMaxY < canvas.height) {
          ctx.moveTo(x, roomMaxY);
          ctx.lineTo(x, canvas.height);
        }
      } else {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
      }
      ctx.stroke();
    }
    
    for (let y = 0; y <= canvas.height; y += majorStep) {
      ctx.beginPath();
      
      if (hasRoom && y >= roomMinY && y <= roomMaxY) {
        if (roomMinX > 0) {
          ctx.moveTo(0, y);
          ctx.lineTo(roomMinX, y);
        }
        if (roomMaxX < canvas.width) {
          ctx.moveTo(roomMaxX, y);
          ctx.lineTo(canvas.width, y);
        }
      } else {
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
      }
      ctx.stroke();
    }
  }, [showGrid, gridSize, walls]);

  const drawWalls = useCallback((ctx) => {
    // Draw architectural style walls - thick, light gray, squared corners
    ctx.strokeStyle = '#d1d5db'; // Light gray fill
    ctx.lineWidth = 20; // Very thick walls like architectural plans
    ctx.lineCap = 'butt'; // Square corners, not rounded
    ctx.lineJoin = 'miter'; // Sharp corner joins
    
    walls.forEach(wall => {
      ctx.beginPath();
      ctx.moveTo(wall.start.x, wall.start.y);
      ctx.lineTo(wall.end.x, wall.end.y);
      ctx.stroke();
    });
    
    // Draw wall outline (darker gray border)
    ctx.strokeStyle = '#9ca3af';
    ctx.lineWidth = 2;
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'miter';
    
    walls.forEach(wall => {
      ctx.beginPath();
      ctx.moveTo(wall.start.x, wall.start.y);
      ctx.lineTo(wall.end.x, wall.end.y);
      ctx.stroke();
    });
    
    // Draw current line if drawing
    if (currentLine && isDrawing) {
      if (tool === 'wall') {
        // Background - light blue
        ctx.strokeStyle = '#bfdbfe';
        ctx.lineWidth = 20;
        ctx.lineCap = 'butt';
        ctx.lineJoin = 'miter';
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(currentLine.start.x, currentLine.start.y);
        ctx.lineTo(currentLine.end.x, currentLine.end.y);
        ctx.stroke();
        
        // Outline
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.setLineDash([10, 5]);
        ctx.beginPath();
        ctx.moveTo(currentLine.start.x, currentLine.start.y);
        ctx.lineTo(currentLine.end.x, currentLine.end.y);
        ctx.stroke();
      } else if (tool === 'door') {
        ctx.strokeStyle = '#059669';
        ctx.lineWidth = 8;
        ctx.setLineDash([8, 4]);
        ctx.beginPath();
        ctx.moveTo(currentLine.start.x, currentLine.start.y);
        ctx.lineTo(currentLine.end.x, currentLine.end.y);
        ctx.stroke();
      } else if (tool === 'window') {
        ctx.strokeStyle = '#0ea5e9';
        ctx.lineWidth = 6;
        ctx.setLineDash([6, 3]);
        ctx.beginPath();
        ctx.moveTo(currentLine.start.x, currentLine.start.y);
        ctx.lineTo(currentLine.end.x, currentLine.end.y);
        ctx.stroke();
      }
      
      ctx.setLineDash([]);
    }
  }, [walls, currentLine, isDrawing, tool]);

  const drawDoors = useCallback((ctx) => {
    doors.forEach(door => {
      // Draw door opening (white background to "cut" through wall)
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 22; // Slightly wider than wall to cut through
      ctx.lineCap = 'butt'; // Square ends to match wall style
      ctx.beginPath();
      ctx.moveTo(door.start.x, door.start.y);
      ctx.lineTo(door.end.x, door.end.y);
      ctx.stroke();
      
      // Draw door frame outline
      ctx.strokeStyle = '#16a34a';
      ctx.lineWidth = 3;
      ctx.lineCap = 'butt';
      ctx.beginPath();
      ctx.moveTo(door.start.x, door.start.y);
      ctx.lineTo(door.end.x, door.end.y);
      ctx.stroke();
      
      // Add door swing arc - architectural style
      const dx = door.end.x - door.start.x;
      const dy = door.end.y - door.start.y;
      const length = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx);
      
      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 2]);
      ctx.beginPath();
      ctx.arc(door.start.x, door.start.y, length, angle, angle + Math.PI / 2);
      ctx.stroke();
      ctx.setLineDash([]);
    });
  }, [doors]);

  const drawWindows = useCallback((ctx) => {
    windows.forEach(window => {
      // Draw window opening (white to cut through wall)
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 22; // Same as door to cut through wall
      ctx.lineCap = 'butt';
      ctx.beginPath();
      ctx.moveTo(window.start.x, window.start.y);
      ctx.lineTo(window.end.x, window.end.y);
      ctx.stroke();
      
      // Draw window sill/frame (light blue background)
      ctx.strokeStyle = '#bae6fd';
      ctx.lineWidth = 16;
      ctx.lineCap = 'butt';
      ctx.beginPath();
      ctx.moveTo(window.start.x, window.start.y);
      ctx.lineTo(window.end.x, window.end.y);
      ctx.stroke();
      
      // Draw window frame (double line architectural style)
      const dx = window.end.x - window.start.x;
      const dy = window.end.y - window.start.y;
      const angle = Math.atan2(dy, dx);
      
      const offsetX = Math.cos(angle + Math.PI / 2) * 4;
      const offsetY = Math.sin(angle + Math.PI / 2) * 4;
      
      ctx.strokeStyle = '#0284c7';
      ctx.lineWidth = 2;
      ctx.lineCap = 'butt';
      
      // Outer frame
      ctx.beginPath();
      ctx.moveTo(window.start.x + offsetX, window.start.y + offsetY);
      ctx.lineTo(window.end.x + offsetX, window.end.y + offsetY);
      ctx.stroke();
      
      // Inner frame
      ctx.beginPath();
      ctx.moveTo(window.start.x - offsetX, window.start.y - offsetY);
      ctx.lineTo(window.end.x - offsetX, window.end.y - offsetY);
      ctx.stroke();
      
      // Center line for window mullion
      ctx.strokeStyle = '#0369a1';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(window.start.x, window.start.y);
      ctx.lineTo(window.end.x, window.end.y);
      ctx.stroke();
    });
  }, [windows]);


  const drawLabels = useCallback((ctx) => {
    ctx.fillStyle = '#6b7280';
    ctx.font = '12px Inter';
    ctx.textAlign = 'center';
    
    walls.forEach((wall, index) => {
      const midX = (wall.start.x + wall.end.x) / 2;
      const midY = (wall.start.y + wall.end.y) / 2;
      const length = Math.sqrt(
        Math.pow(wall.end.x - wall.start.x, 2) + 
        Math.pow(wall.end.y - wall.start.y, 2)
      ) / scale;
      
      ctx.fillText(`${length.toFixed(1)}m`, midX, midY - 5);
    });
    
    // Draw real-time length for current drawing line
    if (currentLine && isDrawing) {
      const currentLength = Math.sqrt(
        Math.pow(currentLine.end.x - currentLine.start.x, 2) + 
        Math.pow(currentLine.end.y - currentLine.start.y, 2)
      ) / scale;
      
      const midX = (currentLine.start.x + currentLine.end.x) / 2;
      const midY = (currentLine.start.y + currentLine.end.y) / 2;
      
      // Highlight background for real-time measurement - different colors for different tools
      let bgColor = '#3b82f6'; // Default blue for walls
      if (tool === 'door') bgColor = '#059669'; // Green for doors
      if (tool === 'window') bgColor = '#0ea5e9'; // Cyan for windows
      
      ctx.fillStyle = bgColor;
      ctx.fillRect(midX - 30, midY - 20, 60, 16);
      
      // White text for contrast
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 12px Inter';
      const label = tool === 'door' ? 'Kapı' : tool === 'window' ? 'Pencere' : 'Duvar';
      ctx.fillText(`${label}: ${currentLength.toFixed(1)}m`, midX, midY - 8);
      
      // Draw measurement points with tool-specific colors
      let pointColor = '#3b82f6'; // Default blue for walls
      if (tool === 'door') pointColor = '#059669'; // Green for doors
      if (tool === 'window') pointColor = '#0ea5e9'; // Cyan for windows
      
      ctx.fillStyle = pointColor;
      ctx.beginPath();
      ctx.arc(currentLine.start.x, currentLine.start.y, 3, 0, 2 * Math.PI);
      ctx.fill();
      
      ctx.beginPath();
      ctx.arc(currentLine.end.x, currentLine.end.y, 3, 0, 2 * Math.PI);
      ctx.fill();
    }
  }, [walls, scale, currentLine, isDrawing, tool]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Clear and set background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw floor texture first
    drawFloorTexture(ctx, canvas);
    
    drawGrid(ctx, canvas);
    drawWalls(ctx);
    drawDoors(ctx);
    drawWindows(ctx);
    drawLabels(ctx);
  }, [drawFloorTexture, drawGrid, drawWalls, drawDoors, drawWindows, drawLabels]);

  // Snap to grid function
  const snapPoint = useCallback((x, y) => {
    if (!snapToGrid) return { x, y };
    
    return {
      x: Math.round(x / gridSize) * gridSize,
      y: Math.round(y / gridSize) * gridSize
    };
  }, [snapToGrid, gridSize]);

  // Calculate room dimensions
  const calculateDimensions = useCallback(() => {
    if (walls.length === 0) {
      setRoomDimensions({ width: 0, height: 0, area: 0 });
      return;
    }
    
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    
    walls.forEach(wall => {
      minX = Math.min(minX, wall.start.x, wall.end.x);
      maxX = Math.max(maxX, wall.start.x, wall.end.x);
      minY = Math.min(minY, wall.start.y, wall.end.y);
      maxY = Math.max(maxY, wall.start.y, wall.end.y);
    });
    
    const width = (maxX - minX) / scale;
    const height = (maxY - minY) / scale;
    const area = width * height;
    
    setRoomDimensions({ width, height, area });
  }, [walls, scale]);

  // Mouse event handlers
  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const handleMouseDown = (e) => {
    const pos = snapPoint(...Object.values(getMousePos(e)));
    
    if (tool === 'wall' || tool === 'door' || tool === 'window') {
      setIsDrawing(true);
      setCurrentLine({ start: pos, end: pos });
    } else if (tool === 'erase') {
      // Find and remove wall/door/window near click
      const threshold = 10;
      
      setWalls(prev => prev.filter(wall => {
        const distToStart = Math.sqrt(Math.pow(wall.start.x - pos.x, 2) + Math.pow(wall.start.y - pos.y, 2));
        const distToEnd = Math.sqrt(Math.pow(wall.end.x - pos.x, 2) + Math.pow(wall.end.y - pos.y, 2));
        return distToStart > threshold && distToEnd > threshold;
      }));
      
      setDoors(prev => prev.filter(door => {
        const distToStart = Math.sqrt(Math.pow(door.start.x - pos.x, 2) + Math.pow(door.start.y - pos.y, 2));
        const distToEnd = Math.sqrt(Math.pow(door.end.x - pos.x, 2) + Math.pow(door.end.y - pos.y, 2));
        return distToStart > threshold && distToEnd > threshold;
      }));
      
      setWindows(prev => prev.filter(window => {
        const distToStart = Math.sqrt(Math.pow(window.start.x - pos.x, 2) + Math.pow(window.start.y - pos.y, 2));
        const distToEnd = Math.sqrt(Math.pow(window.end.x - pos.x, 2) + Math.pow(window.end.y - pos.y, 2));
        return distToStart > threshold && distToEnd > threshold;
      }));
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || (tool !== 'wall' && tool !== 'door' && tool !== 'window')) return;
    
    const pos = snapPoint(...Object.values(getMousePos(e)));
    setCurrentLine(prev => {
      const newLine = { ...prev, end: pos };
      
      // Calculate and update current wall length
      const length = Math.sqrt(
        Math.pow(newLine.end.x - newLine.start.x, 2) + 
        Math.pow(newLine.end.y - newLine.start.y, 2)
      ) / scale;
      setCurrentWallLength(length);
      
      return newLine;
    });
  };

  const handleMouseUp = (e) => {
    if (!isDrawing || (tool !== 'wall' && tool !== 'door' && tool !== 'window')) return;
    
    const pos = snapPoint(...Object.values(getMousePos(e)));
    const finalLine = { ...currentLine, end: pos };
    
    // Check minimum length
    const length = Math.sqrt(
      Math.pow(finalLine.end.x - finalLine.start.x, 2) + 
      Math.pow(finalLine.end.y - finalLine.start.y, 2)
    );
    
    if (length > 10) { // Minimum 10 pixels
      if (tool === 'wall') {
        setWalls(prev => [...prev, finalLine]);
      } else if (tool === 'door') {
        setDoors(prev => [...prev, finalLine]);
      } else if (tool === 'window') {
        setWindows(prev => [...prev, finalLine]);
      }
    }
    
    setIsDrawing(false);
    setCurrentLine(null);
    setCurrentWallLength(0);
  };

  // Clear all
  const clearPlan = () => {
    setWalls([]);
    setDoors([]);
    setWindows([]);
    setCurrentWallLength(0);
  };

  // Auto-close room
  const autoCloseRoom = () => {
    if (walls.length < 3) return;
    
    const firstWall = walls[0];
    const lastWall = walls[walls.length - 1];
    
    // Connect last wall end to first wall start if not already connected
    const threshold = 20;
    const distance = Math.sqrt(
      Math.pow(lastWall.end.x - firstWall.start.x, 2) + 
      Math.pow(lastWall.end.y - firstWall.start.y, 2)
    );
    
    if (distance > threshold) {
      setWalls(prev => [...prev, { start: lastWall.end, end: firstWall.start }]);
    }
  };

  // Effects
  useEffect(() => {
    redraw();
  }, [redraw]);

  useEffect(() => {
    calculateDimensions();
  }, [calculateDimensions]);

  useEffect(() => {
    if (onPlanChange) {
      onPlanChange({
        walls,
        doors,
        windows,
        camera: {
          position: cameraPosition,
          angle: cameraAngle,
          verticalAngle: cameraVerticalAngle
        },
        dimensions: roomDimensions,
        scale
      });
    }
  }, [walls, doors, windows, cameraPosition, cameraAngle, cameraVerticalAngle, roomDimensions, scale, onPlanChange]);

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 overflow-hidden" onWheel={handleWheel}>
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          {/* Tools */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Araçlar:</span>
            {[
              { id: 'wall', label: '🧱 Duvar', color: 'blue' },
              { id: 'door', label: '🚪 Kapı', color: 'green' },
              { id: 'window', label: '🪟 Pencere', color: 'cyan' },
              { id: 'erase', label: '🗑️ Sil', color: 'gray' }
            ].map(toolItem => (
              <button
                key={toolItem.id}
                onClick={() => setTool(toolItem.id)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  tool === toolItem.id
                    ? `bg-${toolItem.color}-100 text-${toolItem.color}-700 border-2 border-${toolItem.color}-300`
                    : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'
                }`}
              >
                {toolItem.label}
              </button>
            ))}
          </div>
          
          {/* Settings */}
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showGrid}
                onChange={(e) => setShowGrid(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Grid Göster</span>
            </label>
            
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={snapToGrid}
                onChange={(e) => setSnapToGrid(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Grid'e Yapış</span>
            </label>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Ölçek:</span>
              <select
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm"
              >
                <option value={80}>1:10</option>
                <option value={40}>1:25</option>
              </select>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={autoCloseRoom}
              className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              Odayı Kapat
            </button>
            <button
              onClick={clearPlan}
              className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors"
            >
              Temizle
            </button>
          </div>
          
          {/* Divider */}
          <div className="w-px h-6 bg-gray-300"></div>
          
          {/* View Mode Toggle - En sağda */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Görünüm:</span>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('2d')}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === '2d' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <MapIcon className="h-4 w-4 mr-2" />
                2D Plan
              </button>
              <button
                onClick={() => setViewMode('3d')}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === '3d' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <CubeIcon className="h-4 w-4 mr-2" />
                3D Görünüm
              </button>
              <button
                onClick={() => setViewMode('split')}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'split' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <ViewColumnsIcon className="h-4 w-4 mr-2" />
                Karma Görünüm
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Canvas Area */}
      <div className="flex-1 flex overflow-hidden" onWheel={handleWheel}>
        {/* Canvas - Ortalanmış */}
        <div className="flex-1 p-4 flex items-center justify-center">
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl shadow-2xl overflow-hidden border border-slate-200">
            <div className="bg-gradient-to-r from-slate-100 to-slate-50 p-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-700 flex items-center">
                  <span className="text-xl mr-3">📐</span>
                  Plan Çizimi
                </h3>
                <div className="flex items-center space-x-3 text-sm text-slate-600 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <span className="font-medium">Ölçek: 1:{scale === 80 ? '10' : '25'}</span>
                  <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
                  <span>Grid: {gridSize}px</span>
                </div>
              </div>
            </div>
            <canvas
              ref={canvasRef}
              width={1000}
              height={700}
              className="cursor-crosshair block bg-white rounded-lg shadow-inner border border-slate-200"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onWheel={handleWheel}
            />
          </div>
        </div>
        
        {/* Info Panel */}
        <div className="w-80 p-4 bg-gradient-to-b from-white to-slate-50 border-l border-slate-200">
          <div className="space-y-6">
            {/* Mini 2D Camera View - En üstte */}
            <MiniCameraView
              walls={walls}
              doors={doors}
              windows={windows}
              cameraPosition={cameraPosition}
              cameraAngle={cameraAngle}
              cameraVerticalAngle={cameraVerticalAngle}
              onCameraMove={setCameraPosition}
              onCameraRotate={setCameraAngle}
              onCameraVerticalRotate={setCameraVerticalAngle}
              scale={scale}
            />
            
            {/* Room Info */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
              <h3 className="font-bold text-blue-900 mb-3 flex items-center">
                <span className="text-lg mr-2">📐</span>
                Oda Bilgileri
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-blue-700">Genişlik:</span>
                  <span className="font-bold text-blue-900 bg-white px-2 py-1 rounded">{roomDimensions.width.toFixed(1)}m</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-blue-700">Derinlik:</span>
                  <span className="font-bold text-blue-900 bg-white px-2 py-1 rounded">{roomDimensions.height.toFixed(1)}m</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-blue-700">Alan:</span>
                  <span className="font-bold text-blue-900 bg-white px-2 py-1 rounded">{roomDimensions.area.toFixed(1)}m²</span>
                </div>
              </div>
            </div>
            
            {/* Statistics */}
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200">
              <h3 className="font-bold text-slate-800 mb-3 flex items-center">
                <span className="text-lg mr-2">📊</span>
                Plan İstatistikleri
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 flex items-center">
                    <span className="w-3 h-3 bg-slate-500 rounded mr-2"></span>
                    Duvarlar:
                  </span>
                  <span className="font-bold text-slate-800 bg-white px-2 py-1 rounded">{walls.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 flex items-center">
                    <span className="w-3 h-3 bg-green-500 rounded mr-2"></span>
                    Kapılar:
                  </span>
                  <span className="font-bold text-slate-800 bg-white px-2 py-1 rounded">{doors.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 flex items-center">
                    <span className="w-3 h-3 bg-cyan-500 rounded mr-2"></span>
                    Pencereler:
                  </span>
                  <span className="font-bold text-slate-800 bg-white px-2 py-1 rounded">{windows.length}</span>
                </div>
              </div>
            </div>
            
            {/* Real-time drawing info */}
            {isDrawing && currentWallLength > 0 && (
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-4 shadow-sm">
                <h3 className="font-bold text-emerald-900 mb-3 flex items-center">
                  <span className="text-lg mr-2">📏</span>
                  Anlık Ölçüm
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-700">Uzunluk:</span>
                    <span className="font-bold text-emerald-900 bg-white px-3 py-1 rounded text-lg">
                      {currentWallLength.toFixed(2)}m
                    </span>
                  </div>
                  <div className="text-xs text-emerald-600 bg-emerald-100 p-2 rounded">
                    💡 Çizimi tamamlamak için mouse'u bırakın
                  </div>
                </div>
              </div>
            )}
            
            {/* Usage Guide - Kısaltılmış */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-100 rounded-xl p-3 border border-amber-200">
              <h3 className="font-bold text-amber-900 mb-2 flex items-center text-sm">
                <span className="mr-2">💡</span>
                Hızlı Rehber
              </h3>
              <div className="text-xs text-amber-800 space-y-1">
                <div>🧱 Tıklayıp sürükleyerek çizim yapın</div>
                <div>🗑️ Silgi ile öğeleri silin</div>
                <div>🔗 "Odayı Kapat" ile tamamlayın</div>
                </div>
                </div>
                </div>
                </div>
                </div>
                </div>
  );
};

// Mini 2D Camera View Component
const MiniCameraView = ({ 
  walls, 
  doors, 
  windows, 
  cameraPosition, 
  cameraAngle, 
  cameraVerticalAngle,
  onCameraMove, 
  onCameraRotate, 
  onCameraVerticalRotate,
  scale 
}) => {
  const miniCanvasRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [miniFloorTexture, setMiniFloorTexture] = useState(null);

  // Load floor texture for mini map
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setMiniFloorTexture(img);
    };
    img.src = '/images/WoodFloor01.jpg';
  }, []);

  // Draw mini map
  const drawMiniMap = useCallback(() => {
    const canvas = miniCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const canvasWidth = 200;
    const canvasHeight = 150;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    
    // Draw background
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    
    // Calculate room bounds
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    if (walls.length > 0) {
      walls.forEach(wall => {
        minX = Math.min(minX, wall.start.x, wall.end.x);
        maxX = Math.max(maxX, wall.start.x, wall.end.x);
        minY = Math.min(minY, wall.start.y, wall.end.y);
        maxY = Math.max(maxY, wall.start.y, wall.end.y);
      });
      
      // Calculate scale to fit the room in mini canvas
      const roomWidth = maxX - minX;
      const roomHeight = maxY - minY;
      const scaleX = (canvasWidth - 40) / roomWidth;
      const scaleY = (canvasHeight - 40) / roomHeight;
      const miniScale = Math.min(scaleX, scaleY);
      
      // Center offset
      const offsetX = (canvasWidth - roomWidth * miniScale) / 2 - minX * miniScale;
      const offsetY = (canvasHeight - roomHeight * miniScale) / 2 - minY * miniScale;
      
      // Draw floor texture first (like main canvas)
      if (miniFloorTexture && walls.length >= 3) {
        ctx.save();
        
        try {
          // Calculate room area
          const padding = 2 * miniScale; // Scale padding for mini map
          const roomMinX = minX * miniScale + offsetX + padding;
          const roomMaxX = maxX * miniScale + offsetX - padding;
          const roomMinY = minY * miniScale + offsetY + padding;
          const roomMaxY = maxY * miniScale + offsetY - padding;
          
          if (roomMaxX > roomMinX && roomMaxY > roomMinY) {
            const roomWidthScaled = roomMaxX - roomMinX;
            const roomHeightScaled = roomMaxY - roomMinY;
            
            // Create pattern and fill room area
            const pattern = ctx.createPattern(miniFloorTexture, 'repeat');
            if (pattern) {
              ctx.save();
              
              // Translate to room area and scale pattern for mini map
              ctx.translate(roomMinX, roomMinY);
              ctx.scale(0.1, 0.1); // Smaller scale for mini map
              
              ctx.fillStyle = pattern;
              ctx.fillRect(0, 0, roomWidthScaled / 0.1, roomHeightScaled / 0.1);
              
              ctx.restore();
            }
          }
        } catch (error) {
          console.log('Mini floor texture rendering error:', error);
        }
        
        ctx.restore();
      }
      
      // Draw walls (thick architectural style like main canvas)
      // Background fill - light gray
      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 8; // Thick walls scaled for mini map
      ctx.lineCap = 'butt';
      ctx.lineJoin = 'miter';
      
      walls.forEach(wall => {
        ctx.beginPath();
        ctx.moveTo(wall.start.x * miniScale + offsetX, wall.start.y * miniScale + offsetY);
        ctx.lineTo(wall.end.x * miniScale + offsetX, wall.end.y * miniScale + offsetY);
        ctx.stroke();
      });
      
      // Wall outline (darker gray border)
      ctx.strokeStyle = '#9ca3af';
      ctx.lineWidth = 1;
      ctx.lineCap = 'butt';
      ctx.lineJoin = 'miter';
      
      walls.forEach(wall => {
        ctx.beginPath();
        ctx.moveTo(wall.start.x * miniScale + offsetX, wall.start.y * miniScale + offsetY);
        ctx.lineTo(wall.end.x * miniScale + offsetX, wall.end.y * miniScale + offsetY);
        ctx.stroke();
      });
      
      // Draw doors (cut through walls like main canvas)
      doors.forEach(door => {
        // Draw door opening (white background to cut through wall)
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 9; // Slightly wider than wall to cut through
        ctx.lineCap = 'butt';
        ctx.beginPath();
        ctx.moveTo(door.start.x * miniScale + offsetX, door.start.y * miniScale + offsetY);
        ctx.lineTo(door.end.x * miniScale + offsetX, door.end.y * miniScale + offsetY);
        ctx.stroke();
        
        // Draw door frame outline
        ctx.strokeStyle = '#16a34a';
        ctx.lineWidth = 1.5;
        ctx.lineCap = 'butt';
        ctx.beginPath();
        ctx.moveTo(door.start.x * miniScale + offsetX, door.start.y * miniScale + offsetY);
        ctx.lineTo(door.end.x * miniScale + offsetX, door.end.y * miniScale + offsetY);
        ctx.stroke();
      });
      
      // Draw windows (cut through walls like main canvas)
      windows.forEach(window => {
        // Draw window opening (white to cut through wall)
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 9; // Same as door to cut through wall
        ctx.lineCap = 'butt';
        ctx.beginPath();
        ctx.moveTo(window.start.x * miniScale + offsetX, window.start.y * miniScale + offsetY);
        ctx.lineTo(window.end.x * miniScale + offsetX, window.end.y * miniScale + offsetY);
        ctx.stroke();
        
        // Draw window sill/frame (light blue background)
        ctx.strokeStyle = '#bae6fd';
        ctx.lineWidth = 6;
        ctx.lineCap = 'butt';
        ctx.beginPath();
        ctx.moveTo(window.start.x * miniScale + offsetX, window.start.y * miniScale + offsetY);
        ctx.lineTo(window.end.x * miniScale + offsetX, window.end.y * miniScale + offsetY);
        ctx.stroke();
        
        // Draw window frame outline
        ctx.strokeStyle = '#0284c7';
        ctx.lineWidth = 1;
        ctx.lineCap = 'butt';
        ctx.beginPath();
        ctx.moveTo(window.start.x * miniScale + offsetX, window.start.y * miniScale + offsetY);
        ctx.lineTo(window.end.x * miniScale + offsetX, window.end.y * miniScale + offsetY);
        ctx.stroke();
      });
      
      // Draw camera
      const camX = cameraPosition.x * miniScale + offsetX;
      const camY = cameraPosition.y * miniScale + offsetY;
      
      // Camera base
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.arc(camX, camY, 4, 0, 2 * Math.PI);
      ctx.fill();
      
      // Camera direction
      const dirLength = 15;
      const dirX = camX + Math.cos(cameraAngle) * dirLength;
      const dirY = camY + Math.sin(cameraAngle) * dirLength;
      
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(camX, camY);
      ctx.lineTo(dirX, dirY);
      ctx.stroke();
      
      // Camera icon
      ctx.fillStyle = '#ffffff';
      ctx.font = '8px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('📷', camX, camY + 2);
    }
  }, [walls, doors, windows, cameraPosition, cameraAngle, miniFloorTexture]);

  // Handle mouse events for camera dragging
  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleMouseMove(e);
  };

  const handleMouseMove = (e) => {
    if (!isDragging && e.type !== 'mousedown') return;
    
    const canvas = miniCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Convert to room coordinates
    if (walls.length > 0) {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      walls.forEach(wall => {
        minX = Math.min(minX, wall.start.x, wall.end.x);
        maxX = Math.max(maxX, wall.start.x, wall.end.x);
        minY = Math.min(minY, wall.start.y, wall.end.y);
        maxY = Math.max(maxY, wall.start.y, wall.end.y);
      });
      
      const roomWidth = maxX - minX;
      const roomHeight = maxY - minY;
      const scaleX = (200 - 40) / roomWidth;
      const scaleY = (150 - 40) / roomHeight;
      const miniScale = Math.min(scaleX, scaleY);
      
      const offsetX = (200 - roomWidth * miniScale) / 2 - minX * miniScale;
      const offsetY = (150 - roomHeight * miniScale) / 2 - minY * miniScale;
      
      const roomX = (x - offsetX) / miniScale;
      const roomY = (y - offsetY) / miniScale;
      
      onCameraMove({ x: roomX, y: roomY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Keyboard controls for camera angle
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          onCameraRotate(cameraAngle - 0.1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          onCameraRotate(cameraAngle + 0.1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          onCameraVerticalRotate(Math.max(-Math.PI/3, cameraVerticalAngle - 0.1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          onCameraVerticalRotate(Math.min(Math.PI/3, cameraVerticalAngle + 0.1));
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cameraAngle, cameraVerticalAngle, onCameraRotate, onCameraVerticalRotate]);

  useEffect(() => {
    drawMiniMap();
  }, [drawMiniMap]);

  return (
    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
      <h3 className="font-bold text-purple-900 mb-3 flex items-center">
        <span className="text-lg mr-2">📷</span>
        Kamera Görünümü
      </h3>
      
      <div className="space-y-3">
        <canvas
          ref={miniCanvasRef}
          width={200}
          height={150}
          className="border border-purple-300 rounded cursor-crosshair bg-white"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
        
        <div className="text-xs text-purple-700 space-y-1">
          <div>🖱️ Kamerayı sürükleyerek hareket ettirin</div>
          <div>⬅️➡️ Yatay dönüş • ⬆️⬇️ Dikey açı</div>
          <div>Dikey açı: {Math.round(cameraVerticalAngle * 180 / Math.PI)}°</div>
        </div>
      </div>
    </div>
  );
};

export default FloorPlanEditor;
